import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, toRefs, watch } from 'vue';
import type { ContentReference } from '@ilteducation/content-collections-types';
import type { Content } from '@ilteducation/content-registry-types';
import ContentAutoComplete from '@/components/ContentAutoComplete.vue';
import eventBus from '@/foundations/services/eventBus';
import useCollectionUtils, {
  pruneReferences,
  type ContentType,
  type HydratedContent,
} from "@/resources/collections/use-collection-utils";
import store from '@/store';

type Emits = {
  (e: 'update:modelValue', value: any): void;
};
type Props = {
  value: any; // modelValue in vue3
  realm?: string;
  context?: string;
  hideShuffleBtn?: boolean;
  contentTypes?: ContentType[];
};
type Hydrated = HydratedContent<Content>;

export default /*#__PURE__*/_defineComponent({
  __name: 'CoverArrayInput',
  props: {
    value: null,
    realm: null,
    context: null,
    hideShuffleBtn: { type: Boolean },
    contentTypes: null
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:modelValue', value: any): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: any; // modelValue in vue3
  realm?: string;
  context?: string;
  hideShuffleBtn?: boolean;
  contentTypes?: ContentType[];
}





// Use `toRefs` to access the prop reactively
const { value: modelValue, realm, context } = toRefs(props);

const { getTitleInRealmLanguage, contentTypes } = useCollectionUtils();

// Hydrated content, tagged with the *reference* type it was loaded for. Ids are not unique
// across content types, so a reference is only matched on id + type.
const hydrated = ref<Hydrated[]>([]);
const localValue = ref<ContentReference[]>([...modelValue.value]);

const isSameRef = (a: ContentReference, b: ContentReference) => a?.id === b?.id && a?.type === b?.type;
const matches = (h: Hydrated, reference: ContentReference) =>
  h.id === reference?.id && h.refType === reference?.type;

const onUpdate = (value: ContentReference[]) => {
  emit('update:modelValue', value);
};

const getRouteFromName = (name: string) => {
  const t = contentTypes.find((ct) => ct.name === name);
  return t?.route;
};

const loadContents = async () => {
  modelValue.value.forEach((contentReference: ContentReference) => {
    const route = getRouteFromName(contentReference.type);
    if (!route) {
      // Collections may legally reference AudioFragment/Image/GigletsBook, which have no
      // admin resource. Skip rather than dispatching `undefined/getOne`.
      console.debug('No admin resource for content type', contentReference.type);
      return;
    }
    if (hydrated.value.some((h) => matches(h, contentReference))) return;
    store
      .dispatch(`${route}/getOne`, { id: contentReference.id })
      .then((response) => {
        if (response?.data) {
          hydrated.value = [
            ...hydrated.value,
            { id: contentReference.id, refType: contentReference.type, content: response.data },
          ];
        }
      })
      .catch((error) => {
        // A failed hydration must never remove the reference itself — see dropRefs.
        console.error(`Failed to load ${contentReference.type} ${contentReference.id}`, error);
      });
  });
};

const getContent = (reference: ContentReference) => hydrated.value.find((h) => matches(h, reference))?.content;

const getCoverUrl = (content?: Content) => {
  if (!content) {
    return '';
  }
  if (content.type === 'BookV1') {
    const cfUrl = process.env.VUE_APP_COVERS_DOMAIN;
    return `https://${cfUrl}/bookCovers/${content.id}/cover.png?width=170`;
  }
  if (content.type === 'VideoV1') {
    const domain = process.env.VUE_APP_COVERS_DOMAIN;
    return `https://${domain}/public/videos/${content.id}/${content.previewImage?.[realm.value]?.fileName}`;
  }
  if (content.type === 'ArticleV1') {
    return content.cover?.url || content.posterUrl;
  }
  if (content.type === 'MaterialV1') {
    const cfUrl = process.env.VUE_APP_COVERS_DOMAIN;
    return `https://${cfUrl}/public/materialPreviews/${content.id}/preview.png?height=100`;
  }
  return '';
};

const onDragEnd = () => {
  modelValue.value.sort((a: ContentReference, b: ContentReference) => {
    const indexA = localValue.value.findIndex((item) => isSameRef(item, a));
    const indexB = localValue.value.findIndex((item) => isSameRef(item, b));
    return indexA - indexB; // Sort by the order in localValue
  });
  onUpdate(modelValue.value);
};

const onAdd = (reference: ContentReference & { data?: Content }) => {
  if (modelValue?.value.find((item: ContentReference) => isSameRef(item, reference))) {
    eventBus.$emit('snackbarV2.open', { text: `${reference.type} already added to collection!`, color: 'warning' });
    return;
  }
  const { data, ...rest } = reference;
  // unshift, not push: newly added content goes to the front of the collection. Reorder by
  // dragging.
  modelValue?.value.unshift(rest);
  localValue.value.unshift(rest);
  if (data) {
    hydrated.value = [...hydrated.value, { id: rest.id, refType: rest.type, content: data }];
  }
  onUpdate(modelValue.value);
};

const onRemove = (reference: ContentReference) => {
  if (!modelValue?.value) {
    return;
  }
  const index = modelValue.value.findIndex((item: ContentReference) => isSameRef(item, reference));
  if (index === -1) {
    return;
  }
  modelValue.value.splice(index, 1);
  localValue.value = localValue.value.filter((item) => !isSameRef(item, reference));
  hydrated.value = hydrated.value.filter((h) => !matches(h, reference));
  onUpdate(modelValue.value);
};

const onShuffle = () => {
  localValue.value = [...localValue.value].sort(() => Math.random() - 0.5);
  onDragEnd();
};

/**
 * Drops the references whose hydrated content fails the realm/context check. Un-hydrated
 * references are kept (see pruneReferences). The previous function pruned down to "ids
 * present in contents", which deleted any reference still loading or failed to load.
 */
const dropFailing = (isAllowed: (content: Content) => boolean) => {
  const surviving = pruneReferences(modelValue.value, hydrated.value, isAllowed);
  if (surviving.length === modelValue.value.length) {
    return;
  }
  hydrated.value = hydrated.value.filter((h) => isAllowed(h.content));
  localValue.value = localValue.value.filter((item) => surviving.some((ref) => isSameRef(ref, item)));
  // Updates modelValue in place to keep the same array reference, which is what Vue 2's
  // reactivity (and this component's v-model) depends on. Until Vue 3.
  modelValue.value.splice(0, modelValue.value.length, ...surviving);
  onUpdate(modelValue.value);
};

watch(modelValue, async (value) => {
  localValue.value = [...value];
  await loadContents();
});

watch(realm, (value) => {
  if (!value) {
    return;
  }
  // Content without the field is kept — we can't prove it doesn't belong.
  dropFailing(
    (content) => !Array.isArray(content?.availabilityRealms) || content.availabilityRealms.includes(value),
  );
});

watch(context, (value) => {
  if (!value) {
    return;
  }
  dropFailing(
    (content) =>
      !Array.isArray(content?.accessRules?.subscriptionType) ||
      content.accessRules.subscriptionType.includes(value),
  );
});

onMounted(async () => {
  await loadContents();
});

return { __sfc: true,emit, props, modelValue, realm, context, getTitleInRealmLanguage, contentTypes, hydrated, localValue, isSameRef, matches, onUpdate, getRouteFromName, loadContents, getContent, getCoverUrl, onDragEnd, onAdd, onRemove, onShuffle, dropFailing, ContentAutoComplete }
}

})